import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BannerService } from '@terminus-ui/banner';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { RadioButton } from 'primeng/radiobutton';
import { CustomerService } from '../customer.service';
import { ManualAddComponent } from '../manual-add/manual-add.component';
import { UploadCsvComponent } from '../upload-csv/upload-csv.component';

type User = {
  name: string;
  email: string;
};

@Component({
  selector: 'lib-add-users-dialog',
  imports: [
    CommonModule,
    ButtonModule,
    FormsModule,
    RadioButton,
    UploadCsvComponent,
    ManualAddComponent,
    DialogModule,
  ],
  templateUrl: './add-users-dialog.component.html',
  styleUrl: './add-users-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DialogService],
})
export class AddUsersDialogComponent implements OnInit {
  uploadMethod: string = 'upload';
  rows: User[] = [];
  private ref = inject(DynamicDialogRef);
  private dialogConfig = inject(DynamicDialogConfig);
  private customerService = inject(CustomerService);
  private bannerService = inject(BannerService);

  customerId: string = '';
  columns: {
    name: string;
    label: string;
    validator: (value: any) => boolean;
  }[] = [
    {
      name: 'name',
      label: 'Name',
      validator: (value: any) => {
        return value && value.length > 0;
      },
    },
    {
      name: 'email',
      label: 'Email',
      validator: (value: any) => {
        return value && value.length > 0 && value.includes('@');
      },
    },
  ];

  ngOnInit(): void {
    this.dialogConfig.data?.customer.subscribe((customer: any) => {
      this.customerId = customer?.customerId;
    });
  }

  cancel() {
    this.ref.close(false);
  }

  upload() {
    if (this.rows.length == 0) {
      this.bannerService.pushBanner({
        isClosable: true,
        isFloating: false,
        title: `No users to add`,
        type: 'error',
        enableAutoDismissWithDuration: 10000,
      });
      return;
    }
    const rows = this.rows.filter((row: any) => {
      if (
        !this.columns[0].validator(row.name) ||
        !this.columns[1].validator(row.email)
      ) {
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: `Invalid user ${row.name}`,
          type: 'error',
          enableAutoDismissWithDuration: 10000,
        });
        return false;
      }
      return true;
    });
    this.customerService.addUsers(this.customerId, rows).subscribe({
      next: () => {
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: `Users added successfully`,
          type: 'success',
          enableAutoDismissWithDuration: 10000,
        });
        this.ref.close(true);
      },
      complete: () => {},
      error: (err) => {
        console.error(err);
      },
    });
  }

  updateRows(rows: User[]) {
    if (rows && rows.length > 0) {
      this.rows = rows;
    }
  }

  onUpload($event: any[]) {
    this.updateRows($event);
  }

  onManual($event: any[]) {
    this.updateRows($event);
  }
}
